import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { startOfYear, subYears } from "date-fns";
import { ControlContainer, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Component, computed, effect, inject, Injector, Signal, signal, untracked } from "@angular/core";

import {
  InputTextComponent,
  DropdownComponent,
  AttachmentComponent,
  DatepickerComponent,
  InputTextDropdownComponent,
} from "src/app/shared";
import { environment } from "src/environments/environment";
import { Permissions } from "src/assets/config/config";
import { AuthService, customValidations, Dropdown, EmploymentType, LookupsService, fallbackOnError } from "src/app/core";

const SHARED = [AttachmentComponent, InputTextComponent, DropdownComponent, DatepickerComponent, InputTextDropdownComponent];

@Component({
  selector: "app-person-request-details",
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule, ...SHARED],
  templateUrl: "./person-request-details.component.html",
  styleUrl: "./person-request-details.component.scss",
})
export class PersonRequestDetailsComponent {
  private injector = inject(Injector);
  private authService = inject(AuthService);
  private activatedRoute = inject(ActivatedRoute);
  private lookupsService = inject(LookupsService);
  private controlContainer = inject(ControlContainer);

  itemId = this.activatedRoute.snapshot.paramMap.get("id");

  genders = toSignal(this.lookupsService.getGenders(), { initialValue: [] });
  countries = toSignal(this.lookupsService.getCountries().pipe(fallbackOnError([])), { initialValue: [] });
  residenceTypes = toSignal(this.lookupsService.getResidenceTypes(), { initialValue: [] });
  employmentTypes = toSignal(this.lookupsService.getEmploymentTypes(), { initialValue: [] });
  isDisabilitySignal = toSignal(this.isDisability?.valueChanges!);

  states: Signal<Dropdown[] | undefined> = signal([]);
  governorates: Signal<Dropdown[] | undefined> = signal([]);
  membershipCardPlans: Signal<Dropdown[] | undefined> | undefined;

  birthDateMaxDate = startOfYear(subYears(new Date(), environment.minAge));

  allowedTypes = [
    { maxSize: 5, type: "png" },
    { maxSize: 5, type: "jpg" },
    { maxSize: 5, type: "jpeg" },
    { maxSize: 5, type: "pdf" },
    { maxSize: 5, type: "word" },
  ];
  yesNoOptions: Dropdown[] = [
    { id: true, nameEn: "Yes", nameAr: "نعم", code: "YES" },
    { id: false, nameEn: "No", nameAr: "لا", code: "NO" },
  ];

  currentPhoneCountryCode = computed(() => {
    return "+968";

    // const countryCode = window.navigator.language.split("-").pop();
    // const country = this.countries().find((c) => c.code === countryCode);

    // if (!country) return null;
    // return country.phoneCode as string;
  });

  get form() {
    return this.controlContainer.control as FormGroup;
  }

  get isDisabilityAttachmentEnabled() {
    return (
      this.form.get("disabilityAttachment")?.enabled || (this.isDisability?.value && this.form.get("disabilityAttachment")?.value?.length)
    );
  }

  private get isDisability() {
    return this.form.get("isDisability");
  }

  private get membershipCardPlanId() {
    return this.form.get("membershipCardPlanId");
  }

  private get totalAmount() {
    return this.form?.get("totalAmount");
  }

  get permanentAddressDetailsCountryId() {
    return this.form?.get("permanentAddressDetails")?.get("countryId");
  }

  get workPlaceDetails() {
    return this.form?.get("workPlaceDetails");
  }

  get isEmploymentTypeEmployee() {
    return this.form.get("employmentType")?.value === EmploymentType.Employee;
  }

  get phoneCountryCode() {
    if (!this.countries().length && !this.form.get("user")?.get("countryCode")?.value) return null;

    const country = this.countries().find((c) => c.phoneCode === this.form.get("user")?.get("countryCode")?.value);

    if (!country) return null;
    return country.code;
  }

  readonly hasPermissionToCreate = computed(() => {
    return Permissions.Memberships.includes(this.authService.userRole()!);
  });

  readonly readonly = computed(() => {
    return false;
    // return !Permissions.Memberships.includes(this.authService.userRole()!)
  });

  constructor() {
    this.lookupsService.assignMembershipCardPlans();
    const membershipCardPlans = this.lookupsService.membershipCardPlans;

    effect(() => {
      this.membershipCardPlans = membershipCardPlans && signal(this.displayMembershipCardPlans(membershipCardPlans()!));

      if (this.countries().length) {
        const isOman = this.countries()
          .find((n) => n.id === this.form.get("permanentAddressDetails")?.get("countryId")?.value)
          ?.code.toLowerCase()
          .includes("om");

        if (this.itemId && isOman) untracked(() => this.bindingLookups());

        if (!this.form.get("user")?.get("countryCode")?.value) {
          this.form.get("user")?.get("countryCode")?.setValue(this.currentPhoneCountryCode());
          this.getPhoneCountryCode(this.currentPhoneCountryCode()!);
        }
      }
    });
  }

  getEmploymentType(event: EmploymentType) {
    if (this.isEmploymentTypeEmployee) {
      this.workPlaceDetails?.enable();
    } else {
      this.workPlaceDetails?.disable();
    }
  }

  getPhoneCountryCode(countryCode: string | number | boolean) {
    const country = this.countries().find((c) => c.phoneCode === countryCode);

    if (country) {
      const countryPhoneRegex = (country as any).phoneCodeRegex;

      this.form
        .get("user")
        ?.get("phone")
        ?.setValidators([Validators.required, customValidations.withCustomMobileNumberRegex(countryPhoneRegex)]);

      this.form.get("user")?.get("phone")?.markAsDirty();
      this.form.get("user")?.get("phone")?.markAllAsTouched();
      this.form.get("user")?.get("phone")?.updateValueAndValidity();
    }
  }

  getGovernorates(id: number) {
    const isOman = this.countries()
      .find((n) => n.id === id)
      ?.code.toLowerCase()
      .includes("om");

    if (isOman) {
      this.governorates = toSignal(this.lookupsService.getGovernorates({ id }).pipe(fallbackOnError(undefined)), {
        injector: this.injector,
      });

      if (!this.itemId) {
        this.form.get("permanentAddressDetails")!.get("governorateId")!.enable();
        this.form.get("permanentAddressDetails")!.get("stateId")!.enable();
      }
    } else {
      this.form.get("permanentAddressDetails")!.get("governorateId")!.disable();
      this.form.get("permanentAddressDetails")!.get("stateId")!.disable();
    }
  }

  getStates(id: number) {
    this.states = toSignal(this.lookupsService.getStates({ id }).pipe(fallbackOnError(undefined)), { injector: this.injector });
  }

  private bindingLookups() {
    this.getGovernorates(this.form.get("permanentAddressDetails")?.get("countryId")?.value!);

    if (this.form.get("permanentAddressDetails")?.get("governorateId")?.value) {
      this.getStates(this.form.get("permanentAddressDetails")?.get("governorateId")?.value!);
    }
  }

  private displayMembershipCardPlans(membershipCardPlans: Dropdown[]) {
    if (!membershipCardPlans) return [];
    if (!this.itemId) {
      this.totalAmount?.reset();
      this.membershipCardPlanId?.reset();
    }

    if (this.isDisabilitySignal()) {
      return membershipCardPlans.filter((membershipCardPlan) => membershipCardPlan.code === "DIS1YI");
    }

    return membershipCardPlans.filter((membershipCardPlan) => {
      return membershipCardPlan.type === "Individuals" && membershipCardPlan.code !== "DIS1YC" && membershipCardPlan.code !== "DIS1YI";
    });
  }
}
